import { useTranslation } from "react-i18next";
import { UserPlus, UserMinus, Clock, ExternalLink, Flag, VolumeX, Volume2, MapPin, Camera } from "lucide-react";
import RichTextView from "@/components/common/RichTextView";
import TipButton from "@/components/TipButton";

/**
 * ProfileHeader — konsistenter Profil-Header für Social / Marketplace / STX.
 *
 * Struktur (identisch für alle 3 Bereiche):
 *   1) Cover (banner_url, 4:1) – Fallback: neutraler Verlauf
 *   2) Runder Avatar überlappt links unten
 *   3) Name (h1) + Follow/Unfollow rechts (nur wenn nicht eigenes Profil)
 *   4) Bio (RichTextView)
 *   5) Stats-Zeile: Follower · Folgt · Beiträge|Artikel|STX-Artikel
 *   6) Cross-Area-Pills (zu den jeweils anderen Bereichen)
 *   7) Melden / Stumm (subtile Chips, nicht bei Own-Profile)
 *
 * KEINE Nachrichten-Aktion (2026-02-24 vom User entfernt).
 */
export default function ProfileHeader({
  profile,
  area,               // "SOCIAL" | "MARKETPLACE" | "STILLTOUCHEDX"
  itemCount = 0,      // Anzahl Beiträge (SOCIAL) oder Listings (MP/STX)
  isOwn = false,
  isFollowing = false,
  followRequested = false,
  followBusy = false,
  onFollow,
  muted = false,
  muteBusy = false,
  onMute,
  onReport,
  onCountClick,       // (type:"followers"|"following") => void — macht Zähler klickbar (eigenes Profil)
  onEditPhoto,        // iter326 · (target:"avatar"|"banner") => void — Kamera-Schnellbearbeitung (eigenes Profil)
  presence = {},
  navigate,
  theme = "light",    // "light" | "dark" (STX = dark)
}) {
  const { t } = useTranslation();
  const isDark = theme === "dark";
  const initials = (profile?.display_name || "?")[0]?.toUpperCase();

  // Theme-Tokens
  const c = isDark
    ? {
        panel: "bg-stx-panel border-stx-border",
        text: "text-stx-text",
        textMuted: "text-stx-text-muted",
        textDim: "text-stx-text-dim",
        border: "border-stx-border",
        pillBase: "border-stx-border text-stx-text-muted hover:border-stx-accent hover:text-stx-accent",
        pillActive: "border-stx-accent bg-stx-accent text-stx-accent-fg",
        followActive: "bg-stx-accent text-stx-accent-fg hover:bg-stx-accent-hover",
        followInactive: "bg-stx-panel border border-stx-border text-stx-text-muted hover:border-stx-accent hover:text-stx-accent",
        mutedOn: "border-stx-accent bg-stx-accent-soft text-stx-accent",
        muteOff: "border-stx-border text-stx-text-muted hover:border-stx-border-strong",
        reportBase: "border-red-500/40 text-red-400 hover:bg-red-500/10",
      }
    : {
        panel: "bg-white border-[#E5E5E5]",
        text: "text-[#0A0A0A]",
        textMuted: "text-[#666]",
        textDim: "text-[#999]",
        border: "border-[#E5E5E5]",
        pillBase: "border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]",
        pillActive: "border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#1A1A1A]",
        followActive: "bg-[#0A0A0A] text-white hover:bg-[#222]",
        followInactive: "bg-white text-[#0A0A0A] border border-[#E5E5E5]",
        mutedOn: "border-[#0A0A0A] bg-[#0A0A0A] text-white",
        muteOff: "border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]",
        reportBase: "border-red-300 text-red-600 hover:bg-red-50",
      };

  const countLabel =
    area === "SOCIAL"
      ? t("public_profile.posts", { defaultValue: "Beiträge" })
      : area === "STILLTOUCHEDX"
      ? t("seller_profile_extra.stx_items", { defaultValue: "STX-Artikel" })
      : t("seller_profile_extra.articles", { defaultValue: "Artikel" });

  return (
    <div data-testid="profile-header" className="w-full pt-3">
      {/* 1) Cover (iter278 · 16:9; iter156 · pt-3 oberhalb für Abstand zum Site-Header) */}
      <div className="relative">
        {profile?.banner_url ? (
          <div className={`w-full ${isDark ? "bg-stx-panel-2" : "bg-[#F0F0F0]"}`} style={{ aspectRatio: "16 / 9" }}>
            <img
              src={profile.banner_url}
              alt=""
              className="w-full h-full object-cover"
              data-testid="profile-header-banner"
            />
          </div>
        ) : (
          <div
            className={`w-full ${isDark ? "bg-gradient-to-b from-stx-accent-soft to-transparent" : "bg-gradient-to-b from-[#F5F5F5] to-[#FAFAFA]"}`}
            style={{ aspectRatio: "16 / 9" }}
          />
        )}
        {isOwn && onEditPhoto && (
          <button
            type="button"
            data-testid="profile-quick-edit-banner"
            onClick={() => onEditPhoto("banner")}
            aria-label={t("edit_profile_extra.cover_label", { defaultValue: "Coverbild ändern" })}
            className="absolute bottom-2 right-2 h-9 w-9 rounded-full bg-black/55 text-white flex items-center justify-center backdrop-blur-sm hover:bg-black/75 transition-colors"
          >
            <Camera size={16} />
          </button>
        )}
      </div>

      {/* 2) Content-Zeile · Info links, Avatar rechts (top-aligned mit dem Namen) */}
      <div className={`px-4 pt-4 pb-4 border-b ${c.border}`}>
        <div className="flex items-start gap-4">
          {/* Info-Block */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 data-testid="profile-header-name" className={`text-lg font-bold truncate ${c.text}`}>
                  {profile?.display_name || "—"}
                </h1>
                {profile?.location && (
                  <p className={`mt-0.5 text-xs inline-flex items-center gap-1 ${c.textMuted}`}>
                    <MapPin size={11} /> {profile.location}
                  </p>
                )}
              </div>
              {!isOwn && (
                <div className="flex gap-2 items-center shrink-0">
                  <TipButton
                    creator={profile}
                    source="PROFILE"
                    variant="chip"
                    size="sm"
                    label={t("public_profile.tip", { defaultValue: "Trinkgeld" })}
                    dataTestid={`profile-tip-btn-${profile?.person_id || "x"}`}
                  />
                  <button
                    data-testid="profile-header-follow"
                    onClick={onFollow}
                    disabled={followBusy}
                    className={`flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium transition-colors active:scale-[0.98] disabled:opacity-60 ${
                      isFollowing || followRequested ? c.followInactive : c.followActive
                    }`}
                  >
                    {isFollowing ? (
                      <>
                        <UserMinus size={14} /> {t("public_profile.unfollow", { defaultValue: "Entfolgen" })}
                      </>
                    ) : followRequested ? (
                      <>
                        <Clock size={14} /> {t("public_profile.requested", { defaultValue: "Angefragt" })}
                      </>
                    ) : (
                      <>
                        <UserPlus size={14} /> {t("public_profile.follow", { defaultValue: "Folgen" })}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {profile?.bio && (
              <RichTextView
                html={profile.bio}
                testId="profile-header-bio"
                className={`mt-2 text-sm leading-relaxed prose prose-sm max-w-none ${c.textMuted}`}
              />
            )}

            <div className="flex gap-4 mt-3 text-sm">
              <button
                type="button"
                data-testid="profile-header-followers"
                onClick={() => onCountClick && onCountClick("followers")}
                disabled={!onCountClick}
                className={`font-semibold ${c.text} ${onCountClick ? "hover:underline cursor-pointer" : "cursor-default"}`}
              >
                {profile?.follower_count || 0}{" "}
                <span className={`font-normal ${c.textDim}`}>{t("public_profile.followers", { defaultValue: "Follower" })}</span>
              </button>
              <button
                type="button"
                data-testid="profile-header-following"
                onClick={() => onCountClick && onCountClick("following")}
                disabled={!onCountClick}
                className={`font-semibold ${c.text} ${onCountClick ? "hover:underline cursor-pointer" : "cursor-default"}`}
              >
                {profile?.following_count || 0}{" "}
                <span className={`font-normal ${c.textDim}`}>{t("public_profile.following", { defaultValue: "Folgt" })}</span>
              </button>
              <span data-testid="profile-header-count" className={`font-semibold ${c.text}`}>
                {itemCount}{" "}
                <span className={`font-normal ${c.textDim}`}>{countLabel}</span>
              </span>
            </div>

            {(presence.SOCIAL || presence.MARKETPLACE || presence.STILLTOUCHEDX) && (() => {
              // P6 · Anonymität: Verweise, die die stilltouchedX-Identität mit Social/MP
              // verknüpfen, nur bei aktivem Opt-in. Social↔Marketplace bleibt frei.
              const optin = !!profile.public_link_optin;
              const onStx = area === "STILLTOUCHEDX";
              const showSocial = area !== "SOCIAL" && presence.SOCIAL && (onStx ? optin : true);
              const showMp = area !== "MARKETPLACE" && presence.MARKETPLACE && (onStx ? optin : true);
              const showStx = area !== "STILLTOUCHEDX" && presence.STILLTOUCHEDX && optin;
              if (!showSocial && !showMp && !showStx) return null;
              const socialSlug = profile.social_handle || profile.handle;
              const stxSlug = profile.canonical_stx_handle || profile.handle;
              return (
              <div className="mt-4 flex flex-wrap gap-2">
                {showSocial && (
                  <button
                    data-testid="cross-area-social"
                    onClick={() => navigate(`/user/${socialSlug}/social`)}
                    className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${c.pillBase}`}
                  >
                    <ExternalLink size={11} /> Social{t("public_profile.profile_arrow", { defaultValue: " →" })}
                  </button>
                )}
                {showMp && (
                  <button
                    data-testid="cross-area-marketplace"
                    onClick={() => navigate(`/user/${socialSlug}/marketplace`)}
                    className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${c.pillBase}`}
                  >
                    <ExternalLink size={11} /> Marketplace{t("public_profile.profile_arrow", { defaultValue: " →" })}
                  </button>
                )}
                {showStx && (
                  <button
                    data-testid="cross-area-stx"
                    onClick={() => navigate(`/user/${stxSlug}/stx`)}
                    className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${c.pillActive}`}
                  >
                    <ExternalLink size={11} /> stilltouchedX{t("public_profile.profile_arrow", { defaultValue: " →" })}
                  </button>
                )}
              </div>
              );
            })()}

            {!isOwn && (onMute || onReport) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {onMute && (
                  <button
                    data-testid="profile-header-mute"
                    onClick={onMute}
                    disabled={muteBusy}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] border transition-colors disabled:opacity-60 ${
                      muted ? c.mutedOn : c.muteOff
                    }`}
                  >
                    {muted ? (
                      <>
                        <Volume2 size={11} /> {t("seller_profile_extra.mute_off", { defaultValue: "Stumm aufheben" })}
                      </>
                    ) : (
                      <>
                        <VolumeX size={11} /> {t("seller_profile_extra.mute_on", { defaultValue: "Stumm schalten" })}
                      </>
                    )}
                  </button>
                )}
                {onReport && (
                  <button
                    data-testid="profile-header-report"
                    onClick={onReport}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] border transition-colors ${c.reportBase}`}
                  >
                    <Flag size={11} /> {t("seller_profile_extra.report", { defaultValue: "Melden" })}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Avatar rechts, top-aligned mit Name */}
          <div className="shrink-0 relative">
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt=""
                data-testid="profile-header-avatar"
                className={`w-28 h-28 rounded-full object-cover border-4 ${isDark ? "border-stx-bg shadow-lg" : "border-white shadow-md"}`}
              />
            ) : (
              <div
                data-testid="profile-header-avatar-fallback"
                className={`w-28 h-28 rounded-full border-4 flex items-center justify-center text-3xl font-bold ${
                  isDark ? "bg-stx-panel border-stx-bg text-stx-text-muted shadow-lg" : "bg-[#E5E5E5] border-white text-[#999] shadow-md"
                }`}
              >
                {initials}
              </div>
            )}
            {isOwn && onEditPhoto && (
              <button
                type="button"
                data-testid="profile-quick-edit-avatar"
                onClick={() => onEditPhoto("avatar")}
                aria-label={t("stx_profile.upload", { defaultValue: "Avatar ändern" })}
                className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center border-2 border-white hover:bg-black/80 transition-colors"
              >
                <Camera size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
